/**
 * «Зафиксировать» (design QuickCapture.dc.html, docs/research/design2-shell-capture.md §3,
 * decisions-log V29, V163): pick what it is about — client, project, team, entity — then the action
 * (digit keys on both levels, Backspace goes back), fill the form and save with ⌘Enter. The form has
 * the action as its title and ← back; no type strip, no «Что произойдёт», no hint lines (V163). The
 * toast offers «Отменить» and, when gaps were added, «Дополнить сейчас» (opens the record's card).
 */
import type { CaptureEventInput, CaptureLookups, CaptureResponse, CaptureType } from '@outsource/shared';
import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent, Ref } from 'react';
import type { CapturePreset } from '../../components/shell/CaptureContext';
import { Button } from '../../components/ui/Button';
import { today as clientToday } from '../../lib/clock';
import { useCreateEntity } from '../entities/queries';
import { useLayer } from '../../lib/layers';
import { useOpenRecord } from '../../lib/navigation';
import { showErrorToast, showToast } from '../../lib/toast';
import { captureFields, showsOptional } from './captureFields';
import { FieldRow } from './FieldRows';
import type { FieldContext } from './FieldRows';
import { FIRST_FIELD, buildEvent, defaults, initialState } from './captureForm';
import type { FormState } from './captureForm';
import { useCaptureLookups, useCapturePreview, useCaptureSave } from './queries';
import { CAPTURE_GROUPS, Tiles, actionLabelOf, pairOf } from './TypeTiles';
import type { CaptureAction, CaptureGroupSpec, CapturePair } from './TypeTiles';

interface QuickCaptureDialogProps {
  readonly preset: CapturePreset;
  readonly seq: number;
  readonly onClose: () => void;
}

/** Capture toasts stay 8 s (design). */
const CAPTURE_TOAST_MS = 8_000;
const EMPTY_LOOKUPS: CaptureLookups = {
  today: '',
  people: [],
  projects: [],
  clients: [],
  contracts: [],
  assignments: [],
  entities: [],
  invoices: [],
};

function useSaveCapture(onClose: () => void) {
  const save = useCaptureSave();
  const openRecord = useOpenRecord();
  const done = (result: CaptureResponse) => {
    onClose();
    const missing = result.newGaps.length;
    const focus = result.focus;
    const actions =
      missing > 0 && focus
        ? [{ label: 'Дополнить сейчас', onClick: () => openRecord(focus.type, focus.id) }]
        : [];
    showToast(`${result.message}${missing > 0 ? ` · не хватает: ${missing}` : ''}`, {
      undoToken: result.undoToken,
      actions,
      durationMs: CAPTURE_TOAST_MS,
    });
  };
  return (event: CaptureEventInput) => {
    if (save.isPending) return;
    save.mutate(event, {
      onSuccess: done,
      onError: (error) => showErrorToast(`Не удалось сохранить: ${error.message.toLowerCase()}`),
    });
  };
}

/** The first field takes focus when a form opens — not when its pair's switch changes it (V233). */
function useFieldRefs(type: CaptureType | null) {
  const refs = useRef(new Map<string, HTMLInputElement | HTMLTextAreaElement>());
  const previous = useRef<CaptureType | null>(null);
  useEffect(() => {
    const isSwitch = !!previous.current && !!type && pairOf(previous.current) === pairOf(type) && pairOf(type) !== null;
    previous.current = type;
    if (type && !isSwitch) refs.current.get(FIRST_FIELD[type])?.focus();
  }, [type]);
  return (key: string): Ref<HTMLInputElement & HTMLTextAreaElement> =>
    (el: HTMLInputElement | HTMLTextAreaElement | null) => {
      if (el) refs.current.set(key, el);
      else refs.current.delete(key);
    };
}

/** «Новое юрлицо» (V143): creates a blank entity, closes the dialog and opens the card to fill it. */
function useNewEntity(onClose: () => void): () => void {
  const create = useCreateEntity();
  const openRecord = useOpenRecord();
  return () =>
    create.mutate(undefined, {
      onSuccess: (result) => {
        onClose();
        showToast(result.message, { undoToken: result.undoToken });
        if (result.id) openRecord('entity', result.id);
      },
    });
}

function CapturePanel({ preset, onClose }: Omit<QuickCaptureDialogProps, 'seq'>) {
  const panelRef = useRef<HTMLDivElement>(null);
  useLayer({ kind: 'dialog', panelRef, onClose, initialFocus: 'panel' });
  const lookupsQuery = useCaptureLookups();
  const lookups = lookupsQuery.data ?? EMPTY_LOOKUPS;
  const today = lookups.today || clientToday();
  const [state, setState] = useState<FormState>(() => initialState(preset, today));
  const refFor = useFieldRefs(state.type);
  const event = state.type && lookupsQuery.data ? buildEvent(state, lookups) : null;
  const preview = useCapturePreview(event);
  const ready = !!event && preview.data?.ready === true;
  const saveEvent = useSaveCapture(onClose);
  const save = () => {
    if (event && ready) saveEvent(event);
  };
  const newEntity = useNewEntity(onClose);
  const [group, setGroup] = useState<CaptureGroupSpec | null>(null);
  const [title, setTitle] = useState<string>(() => (state.type ? actionLabelOf(state.type, state.values) : ''));
  const pickAction = (action: CaptureAction) => {
    if (action.type === 'newEntity') return newEntity();
    setTitle(action.label);
    setState({ type: action.type, values: { ...defaults(action.type, today), ...action.values }, optional: false });
  };
  const back = () => {
    if (state.type) setState({ type: null, values: {}, optional: false });
    else setGroup(null);
  };
  const levelItems = group ? group.actions : CAPTURE_GROUPS;
  const pickTile = (index: number) => {
    if (group) {
      const action = group.actions[index];
      if (action) pickAction(action);
    } else setGroup(CAPTURE_GROUPS[index] ?? null);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      save();
    } else if (!state.type && group && e.key === 'Backspace') {
      e.preventDefault();
      back();
    } else if (!state.type && /^[1-9]$/.test(e.key) && Number(e.key) <= levelItems.length) {
      // The first field takes focus at once; the digit must not land in it.
      e.preventDefault();
      pickTile(Number(e.key) - 1);
    }
  };

  const ctx: FieldContext = {
    values: state.values,
    today,
    set: (patch) => setState((s) => ({ ...s, values: { ...s.values, ...patch } })),
    onEnter: save,
    refFor,
  };
  const set = captureFields(state, lookups);
  const optionalOpen = showsOptional(state, set);
  const pair = pairOf(state.type);
  /** The other event of the pair (V233): its defaults under what is typed, so «Кто» and the entity stay. */
  const switchType = (next: CaptureType) =>
    setState((s) => (s.type === next ? s : { ...s, type: next, values: { ...defaults(next, today), ...s.values } }));

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-label="Зафиксировать"
      tabIndex={-1}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={onKeyDown}
      className="flex max-h-[calc(100vh-88px)] w-[720px] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-3 bg-bg-3 shadow-lg outline-none"
    >
      <Header title={state.type ? title : (group?.label ?? 'Зафиксировать')} onBack={state.type || group ? back : null} onClose={onClose} />
      {!state.type && <Tiles items={levelItems} onPick={pickTile} />}
      {state.type && (
        <>
          <div className="grid min-h-0 flex-1 auto-rows-max grid-cols-[minmax(0,1fr)] content-start gap-3 overflow-y-auto px-6 py-4">
            {pair && <PairSwitch pair={pair} type={state.type} ctx={ctx} onSwitch={switchType} />}
            {[...set.required, ...(optionalOpen ? set.optional : [])].map((spec) => (
              <FieldRow key={`${state.type}-${spec.key}`} spec={spec} ctx={ctx} />
            ))}
            {set.optional.length > 0 && !optionalOpen && (
              <button
                type="button"
                onClick={() => setState((s) => ({ ...s, optional: true }))}
                className="ml-[162px] flex h-8 cursor-pointer items-center gap-2 justify-self-start whitespace-nowrap rounded-2 border border-dashed border-border-2 bg-transparent px-3 text-[12px] font-medium text-fg-2 hover:bg-bg-1"
              >
                {`+ ${set.optionalLabel} — уточню позже`}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 border-t border-border-1 py-3 pl-6 pr-4">
            <span className="flex-1 text-[12px] text-fg-3">⌘Enter — сохранить</span>
            <Button variant="ghost" size="sm" onClick={onClose}>
              Отмена
            </Button>
            <Button variant="primary" size="sm" disabled={!ready} onClick={save}>
              Зафиксировать
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

const PAIR_KEY = 'pair';

/** The switch at the top of a paired form (V233): «Выходит на проект» / «Уходит с проекта», «Оформление» / «Увольнение». */
function PairSwitch({ pair, type, ctx, onSwitch }: {
  readonly pair: CapturePair;
  readonly type: CaptureType | null;
  readonly ctx: FieldContext;
  readonly onSwitch: (next: CaptureType) => void;
}) {
  const switchCtx: FieldContext = {
    ...ctx,
    values: { ...ctx.values, [PAIR_KEY]: type ?? '' },
    set: (patch) => {
      const next = pair.options.find(([value]) => value === patch[PAIR_KEY]);
      if (next) onSwitch(next[0]);
    },
  };
  return <FieldRow spec={{ kind: 'seg', key: PAIR_KEY, label: 'Что', options: pair.options }} ctx={switchCtx} />;
}

/** The level's title — «Зафиксировать», the subject, the action — with ← back below the top (V163). */
function Header({ title, onBack, onClose }: { readonly title: string; readonly onBack: (() => void) | null; readonly onClose: () => void }) {
  return (
    <div className="flex items-center gap-2 pb-3 pl-6 pr-4 pt-4">
      {onBack && (
        <button
          type="button"
          aria-label="Назад"
          onClick={onBack}
          className="-ml-2 size-8 cursor-pointer rounded-ctl border-0 bg-transparent text-[18px] text-fg-2 hover:bg-neutral-100"
        >
          ←
        </button>
      )}
      <h2 className="m-0 min-w-0 flex-1 truncate text-[18px] font-semibold">{title}</h2>
      <button
        type="button"
        aria-label="Закрыть"
        onClick={onClose}
        className="size-8 cursor-pointer rounded-ctl border-0 bg-transparent text-[18px] text-fg-2 hover:bg-neutral-100"
      >
        ×
      </button>
    </div>
  );
}

export function QuickCaptureDialog({ preset, seq, onClose }: QuickCaptureDialogProps) {
  return (
    <div
      className="fixed inset-0 z-[55] flex items-start justify-center bg-scrim-capture pt-14 text-[13px] tabular-nums text-fg-1"
      onClick={onClose}
    >
      <CapturePanel key={seq} preset={preset} onClose={onClose} />
    </div>
  );
}
